import React from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Avatar,
  Container,
  useTheme,
} from "@mui/material";
import SecurityIcon from "@mui/icons-material/Security";
import AddIcon from "@mui/icons-material/Add";

// Import the model options from your existing file
// Assuming modelOptions is exported from TestComponents2.js
import { modelOptions } from "./TestComponents2";

const OurModels = ({
  styles,
  title = "Våra AI-modeller",
  subtitle = "När du genererar material kan du välja mellan följande modeller",
}) => {
  const theme = useTheme();

  return (
    <Box sx={{ py: 0 }}>
      <Typography
        variant="h2"
        sx={{
          fontWeight: 800,
          mb: 2,
          fontSize: { xs: "2rem", md: "2.8rem" },
          textAlign: "center",
        }}
      >
        {title}
      </Typography>
      <Typography
        variant="subtitle"
        sx={{
          color: "text.secondary",
          fontSize: { xs: "1rem", md: "1.3rem" },
          lineHeight: 1.7,
          whiteSpace: "pre-line",
          display: "block",
          width: "100%",
          textAlign: "center",
          mb: 5,
        }}
      >
        {subtitle}
      </Typography>

      <Grid container spacing={3}>
        {modelOptions.map((model) => (
          <Grid item xs={12} sm={6} md={4} key={model.value}>
            <Paper
              elevation={2}
              sx={{
                ...styles.freshBox,
                ...styles.softBoxShadow,
                height: "300px",
                backgroundColor: (theme) => theme.palette.background.paper,
                borderRadius: 4,
                boxShadow: "none",
                backgroundImage: "none",
                p: 4,
                overflow: "hidden",
                mb: 2,
                textAlign: "center",
                position: "relative",
                transition: "all 0.3s ease",
                //cursor: "pointer",
                "&:hover": {
                  transform: "translateY(-4px)",
                  "& .modelDescription": {
                    opacity: 1,
                    transform: "translate3d(0, 0, 0)",
                  },
                  "& .title-text": {
                    transform: "translateY(-8px)",
                  },
                  "& .model-image": {
                    transform: "translateY(-16px)",
                  },
                },
              }}
            >
              {/* Badge (if available) */}
              {model.badge && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 20,
                    right: 20,
                    zIndex: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      backgroundColor: "success.main",
                      color: "white",
                      borderRadius: "20px",
                      px: 1.5,
                      py: 0.5,
                      fontSize: "0.75rem",
                      fontWeight: 600,
                    }}
                  >
                    <SecurityIcon sx={{ fontSize: 16, mr: 0.5 }} />
                    {model.badge}
                  </Box>
                </Box>
              )}

              {/* Model Image */}
              <Box
                className="model-image"
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  width: "100%",
                  my: 4,
                  transition: "transform 0.3s ease",
                }}
              >
                <Avatar
                  src={model.img}
                  alt={model.alt || model.label}
                  sx={{
                    width: 60,
                    height: 60,
                    p: model.value === "Studera.AI" ? 0.5 : 0,
                    bgcolor:
                      model.value === "Studera.AI" ? "transparent" : "#f0f3ff",
                  }}
                />
              </Box>

              {/* Model Title */}
              <Typography
                variant="h5"
                className="title-text"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  fontSize: "1.8rem",
                  transition: "transform 0.3s ease",
                }}
              >
                {model.label}
              </Typography>

              {/* Model Description (hidden by default, shows on hover) */}
              <Box
                className="modelDescription"
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: "50%",
                  p: 3,
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(30, 30, 30, 0.98)"
                      : "rgba(255, 255, 255, 1)",
                  display: "flex",
                  alignItems: "center",
                  opacity: 0,
                  transform: "translate3d(0, 100%, 0)",
                  willChange: "transform, opacity",
                  transition:
                    "transform 350ms cubic-bezier(0.4, 0, 0.2, 1), opacity 350ms cubic-bezier(0.4, 0, 0.2, 1)",
                  borderTop: "1px solid",
                  borderColor: "divider",
                  textAlign: "left",
                  backfaceVisibility: "hidden",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    fontSize: "0.95rem",
                    lineHeight: 1.6,
                    fontWeight: 500,
                    width: "100%",
                  }}
                >
                  {model.description}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        ))}

        {/* Manual "More Models Coming" Card */}
        <Grid item xs={12} sm={6} md={4}>
          <Paper
            elevation={2}
            sx={{
              ...styles.freshBox,
              ...styles.softBoxShadow,
              height: "300px",
              backgroundColor: (theme) => theme.palette.background.paper,
              borderRadius: 4,
              boxShadow: "none",
              backgroundImage: "none",
              p: 4,
              overflow: "hidden",
              mb: 2,
              textAlign: "center",
              position: "relative",
              transition: "all 0.3s ease",
              border: "2px dashed",
              borderColor: "divider",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              "&:hover": {
                transform: "translateY(-4px)",

                "& .add-icon": {
                  transform: "scale(1.1)",
                  color: "primary.main",
                },
              },
            }}
          >
            {/* Plus Icon */}
            <Box
              className="add-icon"
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: 80,
                height: 80,
                borderRadius: "50%",
                backgroundColor: "action.hover",
                mb: 3,
                transition: "all 0.3s ease",
              }}
            >
              <AddIcon
                sx={{
                  fontSize: 40,
                  color: "text.secondary",
                  transition: "all 0.3s ease",
                }}
              />
            </Box>

            {/* Title */}
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                mb: 2,
                fontSize: "1.5rem",
                color: "text.primary",
              }}
            >
              Fler modeller på gång
            </Typography>

            {/* Description */}
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontSize: "1rem",
                lineHeight: 1.6,
                fontWeight: 400,
                textAlign: "center",
                maxWidth: "100%",
              }}
            >
              Vi lägger till fler AI-modeller löpande
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OurModels;
